import type {
  LeadFlowAnswerPolicy,
  LeadFlowAnswerStore,
  LeadFlowAnswers,
} from "../types";

type CreateLeadFlowAnswerStoreOptions = {
  getStorage?: () => Storage | null;
  loadPolicy?: () => Promise<LeadFlowAnswerPolicy>;
};

type StoredLeadFlowAnswers = {
  answers: Record<string, unknown>;
  version: number;
};

const STORAGE_KEY_PREFIX = "lead-flow-answers:";

const STORAGE_VERSION = 1;

const EMPTY_ANSWERS: LeadFlowAnswers = Object.freeze({});

function getSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

// The policy pulls in the contract validators (zod and the postal code list),
// so they load only when a page has answers to persist or restore.
function loadLeadFlowAnswerPolicy(): Promise<LeadFlowAnswerPolicy> {
  return import("./answer-policy");
}

function getStorageKey(sessionKey: string): string {
  return `${STORAGE_KEY_PREFIX}${sessionKey}`;
}

function copyAnswer<TFieldName extends keyof LeadFlowAnswers>(
  source: LeadFlowAnswers,
  target: LeadFlowAnswers,
  fieldName: TFieldName,
) {
  if (fieldName in source) {
    target[fieldName] = source[fieldName];
  } else {
    delete target[fieldName];
  }
}

function isStoredLeadFlowAnswers(
  value: unknown,
): value is StoredLeadFlowAnswers {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const { answers, version } = value as Record<string, unknown>;

  return (
    version === STORAGE_VERSION &&
    typeof answers === "object" &&
    answers !== null &&
    !Array.isArray(answers)
  );
}

/**
 * One answer store per page, shared by every lead flow on it: the page holds
 * one conversation, not one per flow. An answer given in one flow, or a
 * default one flow writes, pre-answers the same question in the others, and
 * a submit from any flow clears the answers for all of them. Only
 * non-personal contract fields reach `sessionStorage`; everything else,
 * including fields the contract gains later, lives in memory and is gone on
 * reload. Stored values are re-validated against the contract on every read.
 *
 * The server snapshot is always empty, so stored answers apply only after
 * hydration, when the URL watcher first calls `resetFor`, and after the
 * contract policy has loaded. Until then an empty snapshot does not mean
 * "nothing stored"; check `hasLoadedAnswers` first.
 */
function createAnswerStore({
  getStorage = getSessionStorage,
  loadPolicy = loadLeadFlowAnswerPolicy,
}: CreateLeadFlowAnswerStoreOptions = {}): LeadFlowAnswerStore {
  let answers = EMPTY_ANSWERS;
  let sessionKey: string | null = null;
  let hasLoaded = false;
  // Bumped by every reset and clear so a restore still waiting on the policy
  // never brings back answers the session has since discarded.
  let sessionGeneration = 0;
  // Questions stay interactive while a restore waits on the policy, so an
  // answer given in that window is the visitor's latest and must survive it.
  let isRestorePending = false;
  const fieldsAnsweredDuringRestore = new Set<keyof LeadFlowAnswers>();
  let policy: LeadFlowAnswerPolicy | null = null;
  let policyPromise: Promise<LeadFlowAnswerPolicy | null> | null = null;
  const listeners = new Set<() => void>();
  const clearListeners = new Set<() => void>();

  function emitChange() {
    for (const listener of listeners) {
      listener();
    }
  }

  // Runs synchronously once the policy has loaded, so callbacks queued while
  // it loads run in the order they were queued. If it fails to load, the
  // store keeps working in memory and nothing is persisted or restored.
  function withPolicy(
    callback: (loadedPolicy: LeadFlowAnswerPolicy | null) => void,
  ) {
    if (policy) {
      callback(policy);
      return;
    }

    policyPromise ??= loadPolicy().then(
      (loadedPolicy) => {
        policy = loadedPolicy;
        return loadedPolicy;
      },
      () => null,
    );
    void policyPromise.then(callback);
  }

  function readStoredEntry(key: string): Record<string, unknown> | null {
    try {
      const rawEntry = getStorage()?.getItem(getStorageKey(key));
      if (!rawEntry) {
        return null;
      }

      const parsedEntry: unknown = JSON.parse(rawEntry);
      return isStoredLeadFlowAnswers(parsedEntry) ? parsedEntry.answers : null;
    } catch {
      return null;
    }
  }

  function removeStoredAnswers(key: string) {
    try {
      getStorage()?.removeItem(getStorageKey(key));
    } catch {
      // Storage can be unavailable or full; answers still work in memory.
    }
  }

  // A tab keeps at most one entry. Leaving a page can unmount its store before
  // it sees the new URL, so the next store removes whatever was left behind.
  function removeOtherStoredAnswers(keptKey: string) {
    try {
      const storage = getStorage();
      if (!storage) {
        return;
      }

      const keptStorageKey = getStorageKey(keptKey);
      const staleStorageKeys = Array.from(
        { length: storage.length },
        (_, index) => storage.key(index),
      ).filter(
        (storageKey): storageKey is string =>
          storageKey !== null &&
          storageKey !== keptStorageKey &&
          storageKey.startsWith(STORAGE_KEY_PREFIX),
      );

      for (const storageKey of staleStorageKeys) {
        storage.removeItem(storageKey);
      }
    } catch {
      // Storage can be unavailable or full; answers still work in memory.
    }
  }

  function writeStoredAnswers(
    loadedPolicy: LeadFlowAnswerPolicy | null,
    generation: number,
  ) {
    if (
      generation !== sessionGeneration ||
      sessionKey === null ||
      loadedPolicy === null
    ) {
      return;
    }

    const persistableAnswers = loadedPolicy.getPersistableAnswers(answers);
    if (Object.keys(persistableAnswers).length === 0) {
      removeStoredAnswers(sessionKey);
      return;
    }

    const entry: StoredLeadFlowAnswers = {
      answers: persistableAnswers,
      version: STORAGE_VERSION,
    };
    try {
      getStorage()?.setItem(getStorageKey(sessionKey), JSON.stringify(entry));
    } catch {
      // Storage can be unavailable or full; answers still work in memory.
    }
  }

  function persistAnswers() {
    if (sessionKey === null) {
      return;
    }

    const generation = sessionGeneration;
    withPolicy((loadedPolicy) => {
      writeStoredAnswers(loadedPolicy, generation);
    });
  }

  function setAnswer<TFieldName extends keyof LeadFlowAnswers>(
    fieldName: TFieldName,
    value: LeadFlowAnswers[TFieldName],
  ) {
    if (isRestorePending) {
      fieldsAnsweredDuringRestore.add(fieldName);
    }

    if (Object.is(answers[fieldName], value)) {
      return;
    }

    const nextAnswers = { ...answers };
    if (value === undefined) {
      delete nextAnswers[fieldName];
    } else {
      nextAnswers[fieldName] = value;
    }
    answers = nextAnswers;

    persistAnswers();
    emitChange();
  }

  function clear() {
    sessionGeneration += 1;
    isRestorePending = false;
    fieldsAnsweredDuringRestore.clear();
    if (sessionKey !== null) {
      removeStoredAnswers(sessionKey);
      hasLoaded = true;
    }
    answers = EMPTY_ANSWERS;
    emitChange();
    for (const listener of clearListeners) {
      listener();
    }
  }

  // Bumps the generation and drops the session key so any `withPolicy`
  // callback still queued for this store cannot rewrite storage after the
  // provider unmounts and the next page has already cleaned other keys.
  function dispose() {
    sessionGeneration += 1;
    isRestorePending = false;
    fieldsAnsweredDuringRestore.clear();
    sessionKey = null;
    hasLoaded = false;
    answers = EMPTY_ANSWERS;
    listeners.clear();
    clearListeners.clear();
  }

  function resetFor(
    nextSessionKey: string,
    sessionParameters: URLSearchParams = new URLSearchParams(),
  ) {
    if (nextSessionKey === sessionKey) {
      return;
    }

    const isFirstSession = sessionKey === null;
    sessionGeneration += 1;
    isRestorePending = false;
    fieldsAnsweredDuringRestore.clear();
    const generation = sessionGeneration;
    sessionKey = nextSessionKey;
    removeOtherStoredAnswers(nextSessionKey);
    const storedEntry = readStoredEntry(nextSessionKey);
    // Any query parameter might be an allowed prefill. Only the policy knows
    // the allowlist, so a session with parameters waits for it to load.
    const hasSessionParameters = sessionParameters.toString() !== "";

    if (!isFirstSession) {
      answers = EMPTY_ANSWERS;
    }

    if (storedEntry === null && !hasSessionParameters) {
      hasLoaded = true;
      if (answers !== EMPTY_ANSWERS) {
        persistAnswers();
      }
      emitChange();
      return;
    }

    hasLoaded = false;
    isRestorePending = true;
    emitChange();

    withPolicy((loadedPolicy) => {
      if (generation !== sessionGeneration) {
        return;
      }

      // The session key includes the query string, so stored answers were
      // given after these prefills and take precedence over them. Anything
      // in memory before the session started was written by code (defaults)
      // before the visitor's answers from before a reload were restored, so
      // both win over it. An answer the visitor gave while the restore was
      // pending is newer than either and wins over both.
      const prefilledAnswers =
        loadedPolicy?.parseQueryPrefill(sessionParameters) ?? {};
      const storedAnswers = storedEntry
        ? (loadedPolicy?.parseStoredAnswers(storedEntry) ?? {})
        : {};
      if (
        Object.keys(prefilledAnswers).length > 0 ||
        Object.keys(storedAnswers).length > 0
      ) {
        const restoredAnswers = {
          ...answers,
          ...prefilledAnswers,
          ...storedAnswers,
        };
        for (const fieldName of fieldsAnsweredDuringRestore) {
          copyAnswer(answers, restoredAnswers, fieldName);
        }
        answers = restoredAnswers;
      }
      isRestorePending = false;
      fieldsAnsweredDuringRestore.clear();
      writeStoredAnswers(loadedPolicy, generation);
      hasLoaded = true;
      emitChange();
    });
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function subscribeToClear(listener: () => void) {
    clearListeners.add(listener);
    return () => {
      clearListeners.delete(listener);
    };
  }

  return {
    clear,
    dispose,
    getServerSnapshot: () => EMPTY_ANSWERS,
    getSnapshot: () => answers,
    hasLoadedAnswers: () => hasLoaded,
    resetFor,
    setAnswer,
    subscribe,
    subscribeToClear,
  };
}

export { createAnswerStore };
